import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Workspace, SetupResult, ROIDiagnosis } from '../lib/types';
import type { BusinessAnalysis } from '../lib/business-analyzer';
import { getAllWorkspaces } from '../lib/services/workspace.service';
import { analyzeBusinessDescription, generateROIDiagnosis } from '../lib/business-analyzer';
import { generateWorkspaceFromAnalysis } from '../lib/workspace-generator';
import { saveROIDiagnosis, getROIDiagnosis } from '../lib/store';

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  setupResult: SetupResult | null;
  analysis: BusinessAnalysis | null;
  roiDiagnosis: ROIDiagnosis | null;
  isLoading: boolean;
  analyzeAndSetup: (description: string) => Promise<SetupResult>;
  selectWorkspace: (id: string) => void;
  refreshWorkspaces: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [setupResult, setSetupResult] = useState<SetupResult | null>(null);
  const [analysis, setAnalysis] = useState<BusinessAnalysis | null>(null);
  const [roiDiagnosis, setRoiDiagnosis] = useState<ROIDiagnosis | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshWorkspaces = () => {
    const allWorkspaces = getAllWorkspaces();
    setWorkspaces(allWorkspaces);
    if (allWorkspaces.length > 0 && !currentWorkspace) {
      const firstWorkspace = allWorkspaces[0];
      setCurrentWorkspace(firstWorkspace);
      // Load ROI diagnosis for this workspace
      const existingDiagnosis = getROIDiagnosis(firstWorkspace.id);
      if (existingDiagnosis) {
        setRoiDiagnosis(existingDiagnosis);
      }
    }
  };

  useEffect(() => {
    refreshWorkspaces();
  }, []);

  const analyzeAndSetup = async (description: string): Promise<SetupResult> => {
    setIsLoading(true);
    try {
      // Simulate AI processing delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1500));

      const analysisResult = analyzeBusinessDescription(description);
      setAnalysis(analysisResult);

      await new Promise(resolve => setTimeout(resolve, 1000));

      const result = generateWorkspaceFromAnalysis(analysisResult);
      setSetupResult(result);
      setCurrentWorkspace(result.workspace);

      // Generate and persist ROI diagnosis
      const diagnosis = generateROIDiagnosis(result.workspace.id, analysisResult);
      saveROIDiagnosis(diagnosis);
      setRoiDiagnosis(diagnosis);

      refreshWorkspaces();

      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const selectWorkspace = (id: string) => {
    const workspace = workspaces.find(w => w.id === id);
    if (workspace) {
      setCurrentWorkspace(workspace);
      // Load ROI diagnosis for selected workspace
      const existingDiagnosis = getROIDiagnosis(id);
      setRoiDiagnosis(existingDiagnosis || null);
    }
  };

  return (
    <WorkspaceContext.Provider value={{
      currentWorkspace,
      workspaces,
      setupResult,
      analysis,
      roiDiagnosis,
      isLoading,
      analyzeAndSetup,
      selectWorkspace,
      refreshWorkspaces,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
